import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { isAmmunitionCategory } from "@/lib/partCategories";
import { listingPath } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import { Crosshair } from "@/components/Crosshair";
import { PayoutBreakdown } from "@/components/PayoutBreakdown";
import { toast } from "@/lib/toast";
import { ShoppingBag, Package, Truck, CheckCircle2, AlertTriangle, Lock, XCircle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";

const money = (c) => `$${((c || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
// Must match backend escrow.BUYER_APPROVAL_STATEMENT verbatim — the exact words we record as evidence.
const APPROVAL_STATEMENT = "I confirm I received this item and it is as described. Release my payment to the seller.";
const TRACKED_CARRIERS = ["UPS", "FedEx", "USPS"];
const SHIP_TYPES = [
  { id: "trackable", label: "Tracked shipment", hint: "UPS / FedEx / USPS with tracking. Funds release 24 hours after the carrier confirms delivery." },
  { id: "untracked", label: "Untracked (USPS, no tracking)", hint: "No delivery scan available. Funds auto-release 7 days after shipping, unless the buyer disputes." },
  { id: "local_pickup", label: "Local pickup / hand-off", hint: "No carrier. Funds auto-release 7 days after hand-off, unless the buyer disputes." },
];

// Seller cancel reasons — must match backend escrow.SELLER_CANCEL_REASONS keys. Note: "buyer
// requested" is deliberately absent — a buyer-requested cancel is created by the buyer.
const SELLER_CANCEL_REASONS = [
  { key: "sold_elsewhere", label: "Item no longer available (sold elsewhere)" },
  { key: "damaged", label: "Item damaged or condition changed" },
  { key: "cannot_ship", label: "Cannot ship to this address" },
  { key: "priced_wrong", label: "Priced incorrectly" },
  { key: "other", label: "Other — written explanation required" },
];
// Mirror of backend escrow._buyer_cancel_state — don't render a cancel control that can't work.
const buyerCanCancel = (o) => {
  if (o.status === "held") return true;
  if (o.status === "shipped" && o.paid_at) return Date.now() < new Date(o.paid_at).getTime() + 60 * 60 * 1000;
  return false;
};

const STATUS = {
  pending_payment: { label: "Awaiting payment", cls: "border-sniper text-muted-foreground" },
  held: { label: "Payment held", cls: "border-volt/50 text-volt" },
  shipped: { label: "Shipped", cls: "border-teal-300/50 text-teal-300" },
  released: { label: "Completed", cls: "border-olive/60 text-olive-bright" },
  release_pending: { label: "Release pending", cls: "border-blaze/50 text-blaze" },
  payout_held: { label: "Payout on hold", cls: "border-[#d3a24e]/60 text-[#d3a24e]" },
  disputed: { label: "Disputed", cls: "border-blaze/50 text-blaze" },
  delivery_exception: { label: "Delivery problem", cls: "border-blaze/60 text-blaze" },
  refunded: { label: "Refunded", cls: "border-sniper text-muted-foreground" },
  cancelled: { label: "Cancelled", cls: "border-sniper text-muted-foreground" },
};

// Carrier tracking layer (informational) — separate from the money status above.
const TRACK_LABEL = {
  pre_transit: "Label created", in_transit: "In transit", out_for_delivery: "Out for delivery",
  available_for_pickup: "Held at facility — awaiting pickup", delivered: "Delivered",
  return_to_sender: "Being returned to sender", failure: "Delivery failed", error: "Carrier error",
  cancelled: "Tracking cancelled", unknown: "Status unknown",
};

function Badge({ status }) {
  const s = STATUS[status] || STATUS.pending_payment;
  return <span data-testid={`order-status-${status}`} className={`font-mono text-[10px] uppercase px-2 py-0.5 border ${s.cls}`}>{s.label}</span>;
}

function OrderThumb({ o }) {
  return (
    <div className="w-16 h-16 shrink-0 border border-sniper bg-obsidian overflow-hidden flex items-center justify-center">
      {o.listing_photo ? <img src={o.listing_photo} alt="" className="w-full h-full object-cover" /> : <Package className="w-6 h-6 text-muted-foreground" />}
    </div>
  );
}

export default function Orders() {
  const { user, platformLabelsEnabled } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState({ purchases: [], sales: [] });
  const [tab, setTab] = useState("purchases");
  const [ship, setShip] = useState({ open: false, order: null, type: "trackable", carrier: "UPS", tracking: "" });
  const [dispute, setDispute] = useState({ open: false, order: null, reason: "" });
  const [approve, setApprove] = useState({ open: false, order: null });
  const [approveConsent, setApproveConsent] = useState(false);
  const [sellerCancel, setSellerCancel] = useState({ open: false, order: null, reason: "", explanation: "", confirm: "", preview: null });
  const [buyerCancel, setBuyerCancel] = useState({ open: false, order: null, reason: "" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/orders/mine").then(({ data }) => setData(data)).catch(() => {});

  useEffect(() => {
    if (!user) { navigate("/login"); return; }
    const checkout = params.get("checkout");
    const sid = params.get("session_id");
    if (checkout === "success" && sid) {
      // The order is CREATED by the Stripe webhook once payment confirms — poll (read-only) until it
      // lands. If it never appears, tell the buyer to contact support (recon catches missed webhooks).
      let tries = 0;
      const poll = () => api.get(`/orders/status/${sid}`).then(({ data }) => {
        if (data && data.status && data.status !== "processing") {
          toast.success("Payment received — held safely until delivery is confirmed.");
          setParams({}, { replace: true }); load();
        } else if (tries++ < 6) {
          setTimeout(poll, 2000);
        } else {
          toast.info("Payment confirmed — your order will appear here in a moment. If it doesn't show up shortly, contact support.");
          setParams({}, { replace: true }); load();
        }
      }).catch(() => { setParams({}, { replace: true }); load(); });
      poll();
    } else if (checkout === "cancel") {
      toast.info("Checkout canceled.");
      setParams({}, { replace: true });
      load();
    } else {
      load();
    }
  }, []); // eslint-disable-line

  const confirmReceived = async (o) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/orders/${o.order_id}/confirm-received`, { consent: true });
      if (data.status === "released") toast.success("Funds released to the seller. This order is complete.");
      else toast.info("Confirmed. The payout to the seller is being processed.");
      setApprove({ open: false, order: null });
      setApproveConsent(false);
      load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const submitShip = async () => {
    const stype = ship.type;
    if (stype === "trackable" && ship.tracking.trim().length < 3) { toast.error("A tracking number is required for tracked shipments."); return; }
    setBusy(true);
    try {
      const carrier = stype === "local_pickup" ? "Local pickup" : (ship.carrier || "USPS");
      await api.post(`/orders/${ship.order.order_id}/ship`, {
        carrier,
        tracking_number: stype === "trackable" ? ship.tracking.trim() : null,
        shipment_type: stype,
      });
      toast.success(stype === "trackable"
        ? "Marked shipped. Funds release 24h after delivery is confirmed (or when the buyer confirms)."
        : "Marked shipped. Funds auto-release in 7 days unless the buyer confirms or disputes.");
      setShip({ open: false, order: null, type: "trackable", carrier: "UPS", tracking: "" });
      load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const submitDispute = async () => {
    if (dispute.reason.trim().length < 3) { toast.error("Please describe the problem."); return; }
    setBusy(true);
    try {
      await api.post(`/orders/${dispute.order.order_id}/dispute`, { reason: dispute.reason.trim() });
      toast.success("Dispute opened. Our team will review and hold the funds.");
      setDispute({ open: false, order: null, reason: "" });
      load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const getLabel = async (o) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/orders/${o.order_id}/get-label`);
      toast.success("Shipping label purchased — it's on your order. The buyer's cancel window is now closed.");
      if (data?.is_ammunition) {
        // Ammo: open the combined print flow (carrier label + DOT Limited Quantity hazmat marking).
        window.open(`/print/ship-label/${o.order_id}`, "_blank", "noopener");
      }
      load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const openSellerCancel = (o) => setSellerCancel({ open: true, order: o, reason: "", explanation: "", confirm: "", preview: null });

  const loadPreview = async (reason) => {
    setSellerCancel((s) => ({ ...s, reason, preview: null }));
    if (!reason) return;
    try {
      const { data } = await api.get(`/orders/${sellerCancel.order.order_id}/cancel-preview`, { params: { reason } });
      setSellerCancel((s) => ({ ...s, preview: data }));
    } catch { /* preview is best-effort */ }
  };

  const submitSellerCancel = async () => {
    const { order, reason, explanation, confirm } = sellerCancel;
    if (!reason) { toast.error("Pick a reason."); return; }
    if (reason === "other" && explanation.trim().length < 3) { toast.error("A written explanation is required for 'Other'."); return; }
    if (confirm.trim().toUpperCase() !== "CANCEL") { toast.error('Type CANCEL to confirm.'); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/orders/${order.order_id}/seller-cancel`, { reason, explanation: explanation.trim() || null });
      toast.success(`Order cancelled and the buyer refunded. ${data.strike?.headline || ""}`);
      setSellerCancel({ open: false, order: null, reason: "", explanation: "", confirm: "", preview: null });
      load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const submitBuyerCancel = async () => {
    const { order, reason } = buyerCancel;
    setBusy(true);
    try {
      await api.post(`/orders/${order.order_id}/buyer-cancel`, { reason: reason.trim() || null });
      toast.success("Order cancelled — you'll be refunded in full. Bank posting time varies.");
      setBuyerCancel({ open: false, order: null, reason: "" });
      load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };


  const list = tab === "purchases" ? data.purchases : data.sales;

  return (
    <div className="min-h-screen text-foreground">
      <Navbar />
      <div className="max-w-[900px] mx-auto px-4 py-8">
        <div className="flex items-center gap-2">
          <ShoppingBag className="w-6 h-6 text-olive-bright" strokeWidth={1.6} />
          <h1 className="font-heading text-4xl font-black tracking-tighter">ORDERS</h1>
        </div>
        <p className="text-sm font-body text-secondary-foreground mt-1 flex items-center gap-1.5">
          <Lock className="w-3.5 h-3.5 text-olive-bright" /> Every purchase is held until delivery is confirmed, then released to the seller.
        </p>

        <div className="flex gap-1 mt-6 border-b border-sniper">
          {["purchases", "sales"].map((t) => (
            <button key={t} onClick={() => setTab(t)} data-testid={`orders-tab-${t}`}
              className={`px-4 py-2.5 font-mono uppercase text-sm border-b-2 -mb-px transition-colors ${tab === t ? "border-olive-bright text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
              {t === "purchases" ? `Purchases (${data.purchases.length})` : `Sales (${data.sales.length})`}
            </button>
          ))}
        </div>

        <div className="mt-5 space-y-3" data-testid="orders-list">
          {list.length === 0 && (
            <div className="border border-sniper bg-surface p-10 text-center font-mono uppercase text-sm text-muted-foreground" data-testid="orders-empty">
              No {tab} yet.
            </div>
          )}

          {list.map((o) => {
            const isBuyer = tab === "purchases";
            const canAct = ["held", "shipped", "release_pending"].includes(o.status);
            // Early release ("Release Funds Now") is only offered while the order is on its way
            // ('shipped'/delivered) — never in 'held' (pre-shipment, nothing in hand) and never in
            // 'release_pending' (a release was already attempted; a buyer re-click can't unblock a
            // seller's payout setup — that retry is handled by the auto-release/admin paths).
            const canRelease = o.status === "shipped";
            const deliveredConfirmed = !!o.delivered_at || o.tracking_status === "delivered";
            return (
              <div key={o.order_id} className="border border-sniper bg-surface p-4" data-testid={`order-${o.order_id}`}>
                <div className="flex items-start gap-4">
                  <OrderThumb o={o} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <Link to={listingPath(o)} className="font-heading text-lg text-foreground hover:text-olive-bright truncate">{o.listing_title || "Listing"}</Link>
                      <Badge status={o.status} />
                    </div>
                    <div className="mt-1 flex items-center gap-3 flex-wrap text-[11px] font-mono uppercase text-muted-foreground">
                      {isBuyer ? (
                        <span>Seller: <Link to={`/u/${o.seller_username}`} className="text-secondary-foreground hover:text-foreground">@{o.seller_username}</Link></span>
                      ) : (
                        <span>Buyer: <Link to={`/u/${o.buyer_username}`} className="text-secondary-foreground hover:text-foreground">@{o.buyer_username}</Link></span>
                      )}
                      <span className="text-foreground">{money(o.amount_cents)}</span>
                      {!isBuyer && <span>payout <span className="text-olive-bright">{money(o.payout_cents)}</span> · fee {money(o.commission_cents)}</span>}
                    </div>
                    {o.tracking_number ? (
                      <p className="mt-1 text-[11px] font-mono uppercase text-teal-300 flex items-center gap-1.5"><Truck className="w-3.5 h-3.5" /> {o.carrier} · {o.tracking_number}</p>
                    ) : (["shipped", "released"].includes(o.status) && o.carrier && (
                      <p className="mt-1 text-[11px] font-mono uppercase text-teal-300 flex items-center gap-1.5"><Truck className="w-3.5 h-3.5" /> {o.shipment_type === "local_pickup" ? "Local pickup / hand-off" : `${o.carrier} · no tracking`}</p>
                    ))}
                    {o.tracking_status && o.tracking_status !== "delivered" && (
                      <p className="mt-1 text-[11px] font-mono flex items-center gap-1.5" data-testid={`order-tracking-${o.order_id}`}>
                        <span className={o.tracking_status === "available_for_pickup" ? "text-[#d3a24e]" : "text-secondary-foreground"}>{TRACK_LABEL[o.tracking_status] || o.tracking_status}</span>
                        {o.tracking_last_location && <span className="text-muted-foreground">· {o.tracking_last_location}</span>}
                      </p>
                    )}
                    {o.tracking_status === "delivered" && o.delivered_at && (
                      <p className="mt-1 text-[11px] font-mono text-olive-bright flex items-center gap-1.5" data-testid={`order-delivered-${o.order_id}`}>
                        <CheckCircle2 className="w-3.5 h-3.5" /> Delivered {String(o.delivered_at).slice(0, 10)}{o.delivery_signed_by ? ` · signed by ${o.delivery_signed_by}` : ""}
                      </p>
                    )}
                    {o.delivery_attempted_at && o.status !== "delivery_exception" && (
                      <p className="mt-1 text-[11px] font-mono text-volt" data-testid={`order-attempt-${o.order_id}`}>
                        Delivery attempted {String(o.delivery_attempted_at).slice(0, 10)} — not completed yet.
                      </p>
                    )}
                    {o.tracking_status === "available_for_pickup" && (
                      <p className="mt-2 text-[12px] font-body text-[#d3a24e] border-l-2 border-[#d3a24e]/60 pl-2.5" data-testid={`held-at-facility-${o.order_id}`}>
                        {isBuyer
                          ? "The carrier is holding this at a facility for pickup — please collect it before it's returned to the sender."
                          : "The carrier is holding this at a facility for the buyer to pick up."}
                      </p>
                    )}
                    {o.status === "delivery_exception" && (
                      <p className="mt-2 text-[12px] font-body text-blaze border-l-2 border-blaze/60 pl-2.5" data-testid={`delivery-exception-${o.order_id}`}>
                        {isBuyer
                          ? "There's a delivery problem with this order (the carrier couldn't complete it). Our team is reviewing it — your payment is safe and held."
                          : "There's a delivery problem — the carrier couldn't complete delivery. Our team is reviewing before any payout."}
                      </p>
                    )}
                    {o.status === "disputed" && o.dispute_reason && (
                      <p className="mt-1 text-[11px] font-mono text-blaze">Dispute: {o.dispute_reason}</p>
                    )}
                    {o.status === "held" && o.awaiting_seller_payout && (
                      isBuyer ? (
                        <p className="mt-2 text-[12px] font-body text-secondary-foreground border-l-2 border-volt/50 pl-2.5" data-testid={`awaiting-payout-buyer-${o.order_id}`}>
                          Payment received — waiting on the seller to confirm shipping details. Your money is held safely until delivery is confirmed.
                        </p>
                      ) : (
                        <p className="mt-2 text-[12px] font-body text-volt border-l-2 border-volt/60 pl-2.5" data-testid={`awaiting-payout-seller-${o.order_id}`}>
                          Add your payout details to receive {money(o.amount_cents)} and ship this order. Set them up in your dashboard — until then the sale is held.
                        </p>
                      )
                    )}

                    {o.status === "payout_held" && (
                      isBuyer ? (
                        <p className="mt-2 text-[12px] font-body text-secondary-foreground border-l-2 border-[#d3a24e]/50 pl-2.5" data-testid={`payout-held-buyer-${o.order_id}`}>
                          Delivered — your order is complete and nothing is needed from you. The seller's payout is briefly on hold.
                        </p>
                      ) : (
                        <p className="mt-2 text-[12px] font-body text-[#d3a24e] border-l-2 border-[#d3a24e]/60 pl-2.5" data-testid={`payout-held-seller-${o.order_id}`}>
                          {o.payout_hold_reason === "reconnect_required" ? (
                            <>Payout on hold — your Stripe payout account needs to be reconnected. Reconnect payouts from your dashboard to release {money(o.payout_cents)}. Your money is safe.</>
                          ) : o.payout_hold_reason === "payout_unverifiable" || o.payout_hold_reason === "payout_not_ready" ? (
                            <>Payout on hold — we couldn't confirm your payout account is ready. Finish payout setup from your dashboard to release {money(o.payout_cents)}. Your money is safe.</>
                          ) : (
                            <>Payout on hold pending identity re-verification. Re-verify your ID to release {money(o.payout_cents)} — start it from your dashboard. Your money is safe; this is a review step, not a loss.</>
                          )}
                        </p>
                      )
                    )}

                    {!isBuyer && ["held", "shipped", "release_pending", "released", "disputed", "payout_held"].includes(o.status) && (
                      <div className="mt-3 max-w-xs" data-testid={`sale-breakdown-${o.order_id}`}>
                        <PayoutBreakdown
                          amountCents={o.amount_cents}
                          platformFeeCents={o.platform_fee_cents ?? o.commission_cents}
                          processingCents={o.stripe_fee_cents ?? o.stripe_fee_estimated_cents ?? 0}
                          payoutCents={o.payout_cents}
                          estimated={o.stripe_fee_cents == null}
                          testId={`sale-payout-${o.order_id}`}
                        />
                      </div>
                    )}

                    {/* actions */}
                    <div className="mt-3 flex flex-wrap gap-2">
                      {isBuyer && canAct && (
                        <>
                          {canRelease && (
                            <button disabled={busy} onClick={() => { setApproveConsent(false); setApprove({ open: true, order: o }); }} data-testid={`confirm-received-${o.order_id}`}
                              className="inline-flex items-center gap-1.5 bg-olive hover:bg-olive-bright disabled:opacity-50 text-white font-heading font-bold uppercase text-sm px-4 py-2 transition-colors">
                              <CheckCircle2 className="w-4 h-4" /> Release Funds Now
                            </button>
                          )}
                          <button disabled={busy} onClick={() => setDispute({ open: true, order: o, reason: "" })} data-testid={`dispute-${o.order_id}`}
                            className="inline-flex items-center gap-1.5 border border-blaze/50 text-blaze hover:bg-blaze/10 font-mono uppercase text-sm px-4 py-2 transition-colors">
                            <AlertTriangle className="w-4 h-4" /> Open dispute
                          </button>
                          {buyerCanCancel(o) && (
                            <button disabled={busy} onClick={() => setBuyerCancel({ open: true, order: o, reason: "" })} data-testid={`buyer-cancel-${o.order_id}`}
                              className="inline-flex items-center gap-1.5 border border-muted-foreground/40 text-muted-foreground hover:text-foreground hover:border-foreground/40 font-mono uppercase text-sm px-4 py-2 transition-colors">
                              <XCircle className="w-4 h-4" /> Cancel order
                            </button>
                          )}
                        </>
                      )}
                      {!isBuyer && o.status === "held" && o.awaiting_seller_payout && (
                        <button onClick={() => navigate("/dashboard")} data-testid={`payout-setup-${o.order_id}`}
                          className="inline-flex items-center gap-1.5 bg-volt hover:brightness-110 text-black font-heading font-bold uppercase text-sm px-4 py-2 transition-all">
                          <Lock className="w-4 h-4" /> Add payout details
                        </button>
                      )}
                      {!isBuyer && o.status === "held" && !o.awaiting_seller_payout && (
                        <>
                          {platformLabelsEnabled && o.shipping_rate_id && !o.tracking_number && (
                            <button disabled={busy} onClick={() => getLabel(o)} data-testid={`get-label-${o.order_id}`}
                              className="inline-flex items-center gap-1.5 bg-olive hover:bg-olive-bright disabled:opacity-50 text-white font-heading font-bold uppercase text-sm px-4 py-2 transition-colors">
                              <Package className="w-4 h-4" /> Get shipping label
                            </button>
                          )}
                          <button disabled={busy} onClick={() => setShip({ open: true, order: o, type: "trackable", carrier: "UPS", tracking: "" })} data-testid={`ship-${o.order_id}`}
                            className="inline-flex items-center gap-1.5 border border-olive/60 text-olive-bright hover:bg-olive/10 disabled:opacity-50 font-mono uppercase text-sm px-4 py-2 transition-colors">
                            <Truck className="w-4 h-4" /> Mark shipped
                          </button>
                        </>
                      )}
                      {!isBuyer && ["held", "shipped", "release_pending"].includes(o.status) && (
                        <button disabled={busy} onClick={() => openSellerCancel(o)} data-testid={`seller-cancel-${o.order_id}`}
                          className="inline-flex items-center gap-1.5 border border-blaze/40 text-blaze hover:bg-blaze/10 font-mono uppercase text-sm px-4 py-2 transition-colors">
                          <XCircle className="w-4 h-4" /> Cancel order
                        </button>
                      )}
                      {!isBuyer && o.status === "shipped" && <span className="text-[11px] font-mono uppercase text-muted-foreground py-2">Awaiting buyer confirmation…</span>}
                      {o.status === "released" && (
                        <span className="text-[11px] font-mono uppercase text-olive-bright py-2" data-testid={`order-released-${o.order_id}`}>
                          {isBuyer
                            ? "Order complete · payment released"
                            : `Funds released · paid out ${money(o.payout_cents)}${o.released_at ? ` · ${String(o.released_at).slice(0, 10)}` : ""}`}
                        </span>
                      )}
                      {o.status === "disputed" && <span className="text-[11px] font-mono uppercase text-blaze py-2">Under admin review</span>}
                      {o.status === "refunded" && <span className="text-[11px] font-mono uppercase text-muted-foreground py-2">Refunded to buyer</span>}
                    </div>
                    {!isBuyer && isAmmunitionCategory(o.category) && ["held", "shipped", "release_pending"].includes(o.status) && (
                      <div className="mt-3 border border-[#d3a24e]/50 bg-[#d3a24e]/10 px-3 py-2.5 flex items-start gap-2.5" data-testid={`ammo-hazmat-note-${o.order_id}`}>
                        <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5 text-[#d3a24e]" />
                        <div className="min-w-0">
                          <p className="font-mono text-[11px] uppercase tracking-wide text-[#e6c07a]">Ammunition — hazmat marking required</p>
                          <p className="text-[12px] text-muted-foreground mt-1 leading-relaxed">
                            Affix the <b className="text-foreground">DOT Limited Quantity</b> mark to the outside of the box before shipping. Ships <b className="text-foreground">UPS / FedEx Ground</b> only — USPS won't carry ammo. Your platform label prints with the marking on page 2.
                          </p>
                          {o.tracking_number && (
                            <button onClick={() => window.open(`/print/ship-label/${o.order_id}`, "_blank", "noopener")} data-testid={`reprint-label-${o.order_id}`}
                              className="mt-2 inline-flex items-center gap-1.5 border border-[#d3a24e]/60 text-[#e6c07a] hover:bg-[#d3a24e]/10 font-mono uppercase text-[11px] px-3 py-1.5 transition-colors">
                              <Package className="w-3.5 h-3.5" /> Reprint label + marking
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Ship dialog */}
      <Dialog open={ship.open} onOpenChange={(v) => setShip((s) => ({ ...s, open: v }))}>
        <DialogContent className="bg-surface border-sniper text-foreground rounded-none">
          <DialogHeader><DialogTitle className="font-heading uppercase flex items-center gap-2"><Truck className="w-5 h-5 text-olive-bright" /> Mark Shipped</DialogTitle></DialogHeader>
          <DialogDescription className="text-xs font-mono uppercase text-muted-foreground">How is the item going out? Release timing depends on the shipment type.</DialogDescription>

          <div className="space-y-2" data-testid="ship-type-group">
            {SHIP_TYPES.map((t) => (
              <button key={t.id} type="button" data-testid={`ship-type-${t.id}`}
                onClick={() => setShip((s) => ({ ...s, type: t.id }))}
                className={`w-full text-left border px-3 py-2.5 transition-colors ${ship.type === t.id ? "border-olive-bright bg-olive/10" : "border-sniper hover:border-white/40"}`}>
                <div className="flex items-center gap-2">
                  <span className={`w-3 h-3 shrink-0 rounded-full border ${ship.type === t.id ? "bg-olive-bright border-olive-bright" : "border-muted-foreground"}`} />
                  <span className="font-mono text-sm uppercase text-foreground">{t.label}</span>
                </div>
                <p className="mt-1 pl-5 text-[10px] font-mono uppercase text-muted-foreground leading-snug">{t.hint}</p>
              </button>
            ))}
          </div>

          {ship.type !== "local_pickup" && (
            <>
              <label className="font-mono text-[10px] uppercase text-muted-foreground mt-1">Carrier</label>
              <select data-testid="ship-carrier" value={ship.carrier} onChange={(e) => setShip((s) => ({ ...s, carrier: e.target.value }))}
                className="w-full bg-input border border-sniper focus:border-olive outline-none px-3 py-2.5 text-foreground font-mono text-sm transition-colors">
                {TRACKED_CARRIERS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </>
          )}
          {ship.type === "trackable" && (
            <>
              <label className="font-mono text-[10px] uppercase text-muted-foreground mt-2">Tracking Number</label>
              <input data-testid="ship-tracking" value={ship.tracking} onChange={(e) => setShip((s) => ({ ...s, tracking: e.target.value }))}
                placeholder="1Z999AA10123456784" className="w-full bg-input border border-sniper focus:border-olive outline-none px-3 py-2.5 text-foreground font-mono text-sm transition-colors" />
            </>
          )}
          <DialogFooter>
            <button onClick={submitShip} disabled={busy} data-testid="ship-submit" className="bg-olive hover:bg-olive-bright disabled:opacity-50 text-white font-heading font-bold uppercase px-6 py-2.5 transition-colors">Confirm shipment</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dispute dialog */}
      <Dialog open={dispute.open} onOpenChange={(v) => setDispute((s) => ({ ...s, open: v }))}>
        <DialogContent className="bg-surface border-sniper text-foreground rounded-none">
          <DialogHeader><DialogTitle className="font-heading uppercase flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-blaze" /> Open Dispute</DialogTitle></DialogHeader>
          <DialogDescription className="text-xs font-mono uppercase text-muted-foreground">Funds stay held while our team reviews. Describe what went wrong.</DialogDescription>
          <textarea rows={4} data-testid="dispute-reason" value={dispute.reason} onChange={(e) => setDispute((s) => ({ ...s, reason: e.target.value }))}
            placeholder="Item not as described, never arrived, damaged…" className="w-full bg-input border border-sniper focus:border-olive outline-none px-3 py-2.5 text-foreground font-body text-sm transition-colors" />
          <DialogFooter>
            <button onClick={submitDispute} disabled={busy} data-testid="dispute-submit" className="bg-blaze hover:brightness-110 disabled:opacity-50 text-white font-heading font-bold uppercase px-6 py-2.5 transition-colors">Submit dispute</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Approve / confirm-received attestation dialog */}
      <Dialog open={approve.open} onOpenChange={(v) => { setApprove((s) => ({ ...s, open: v })); if (!v) setApproveConsent(false); }}>
        <DialogContent className="bg-surface border-sniper text-foreground rounded-none" data-testid="approve-dialog">
          <DialogHeader><DialogTitle className="font-heading uppercase flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-olive-bright" /> Release funds now</DialogTitle></DialogHeader>
          <DialogDescription className="text-xs font-mono uppercase text-muted-foreground">
            Paying the seller {approve.order ? money(approve.order.amount_cents) : ""} — this can't be undone.
          </DialogDescription>

          {/* Plain-language protection warning — releasing early ENDS escrow protection. */}
          <div className="border border-blaze/50 bg-blaze/5 px-3 py-2.5 flex items-start gap-2.5" data-testid="approve-protection-warning">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5 text-blaze" />
            <p className="text-[12px] font-body text-secondary-foreground leading-relaxed">
              This immediately releases your payment to the seller and <b className="text-foreground">ends escrow protection</b> on this order.
              If the item hasn't arrived or isn't as described, <b className="text-foreground">open a dispute instead</b> — don't release.
            </p>
          </div>

          {/* Extra caution when the carrier hasn't confirmed delivery yet. */}
          {approve.order && !(approve.order.delivered_at || approve.order.tracking_status === "delivered") && (
            <div className="border border-[#d3a24e]/50 bg-[#d3a24e]/10 px-3 py-2.5 flex items-start gap-2.5" data-testid="approve-not-delivered-warning">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5 text-[#d3a24e]" />
              <p className="text-[12px] font-body text-[#e6c07a] leading-relaxed">
                The carrier hasn't confirmed delivery yet. Only release if you already have the item in hand and it's as described.
              </p>
            </div>
          )}

          <div className="border-l-2 border-olive-bright bg-olive/5 pl-3 py-2.5">
            <p className="font-body text-sm text-foreground" data-testid="approve-statement">“{APPROVAL_STATEMENT}”</p>
          </div>

          {/* Required, active consent — never pre-checked, never a click-through. */}
          <label className="flex items-start gap-2.5 cursor-pointer select-none" data-testid="approve-consent-label">
            <input type="checkbox" checked={approveConsent} onChange={(e) => setApproveConsent(e.target.checked)}
              data-testid="approve-consent-checkbox"
              className="mt-0.5 w-4 h-4 accent-olive-bright shrink-0" />
            <span className="text-[12px] font-body text-foreground leading-relaxed">
              I've inspected the item and it's as described. I understand this releases my payment now and ends escrow protection on this order.
            </span>
          </label>

          <p className="text-[11px] font-mono uppercase text-muted-foreground">
            We record this confirmation (statement, consent, time &amp; IP) as proof of delivery.
          </p>
          <DialogFooter>
            <button onClick={() => confirmReceived(approve.order)} disabled={busy || !approveConsent} data-testid="approve-submit"
              className="bg-olive hover:bg-olive-bright disabled:opacity-40 disabled:cursor-not-allowed text-white font-heading font-bold uppercase px-6 py-2.5 transition-colors">
              {busy ? "Releasing…" : "Release funds now"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Seller cancel dialog — reason required, consequence preview, type CANCEL to confirm */}
      <Dialog open={sellerCancel.open} onOpenChange={(v) => setSellerCancel((s) => ({ ...s, open: v }))}>
        <DialogContent className="bg-surface border-sniper text-foreground rounded-none" data-testid="seller-cancel-dialog">
          <DialogHeader><DialogTitle className="font-heading uppercase flex items-center gap-2"><XCircle className="w-5 h-5 text-blaze" /> Cancel this sale</DialogTitle></DialogHeader>
          <DialogDescription className="text-xs font-mono uppercase text-muted-foreground">
            Cancelling a paid order refunds the buyer in full and carries a strike. Pick the reason that fits.
          </DialogDescription>
          <div className="space-y-1.5" data-testid="seller-cancel-reasons">
            {SELLER_CANCEL_REASONS.map((r) => (
              <button key={r.key} type="button" data-testid={`seller-cancel-reason-${r.key}`}
                onClick={() => loadPreview(r.key)}
                className={`w-full text-left border px-3 py-2 transition-colors flex items-center gap-2 ${sellerCancel.reason === r.key ? "border-blaze bg-blaze/10" : "border-sniper hover:border-white/40"}`}>
                <span className={`w-3 h-3 shrink-0 rounded-full border ${sellerCancel.reason === r.key ? "bg-blaze border-blaze" : "border-muted-foreground"}`} />
                <span className="font-mono text-xs uppercase">{r.label}</span>
              </button>
            ))}
          </div>
          {sellerCancel.reason === "other" && (
            <textarea rows={2} data-testid="seller-cancel-explanation" value={sellerCancel.explanation}
              onChange={(e) => setSellerCancel((s) => ({ ...s, explanation: e.target.value }))}
              placeholder="Explain what happened…" className="w-full bg-input border border-sniper focus:border-olive outline-none px-3 py-2 text-foreground font-body text-sm transition-colors" />
          )}
          {sellerCancel.preview && (
            <div className="border-l-2 border-blaze/60 bg-blaze/5 pl-3 py-2" data-testid="seller-cancel-preview">
              <p className="font-mono text-[10px] uppercase text-muted-foreground">Strike {sellerCancel.preview.strike_number} · consequence</p>
              <p className="font-body text-sm text-foreground mt-0.5">{sellerCancel.preview.headline}</p>
              {sellerCancel.preview.processing_cents_if_strike2 > 0 && (
                <p className="font-mono text-[11px] text-[#d3a24e] mt-1">Processing recovered from next payout: {money(sellerCancel.preview.processing_cents_if_strike2)}</p>
              )}
            </div>
          )}
          <label className="font-mono text-[10px] uppercase text-muted-foreground mt-1">Type CANCEL to confirm</label>
          <input data-testid="seller-cancel-confirm" value={sellerCancel.confirm}
            onChange={(e) => setSellerCancel((s) => ({ ...s, confirm: e.target.value }))}
            placeholder="CANCEL" className="w-full bg-input border border-sniper focus:border-blaze outline-none px-3 py-2.5 text-foreground font-mono text-sm transition-colors" />
          <DialogFooter>
            <button onClick={submitSellerCancel} disabled={busy || !sellerCancel.reason || sellerCancel.confirm.trim().toUpperCase() !== "CANCEL"} data-testid="seller-cancel-submit"
              className="bg-blaze hover:brightness-110 disabled:opacity-40 disabled:cursor-not-allowed text-white font-heading font-bold uppercase px-6 py-2.5 transition-all">
              {busy ? "Cancelling…" : "Cancel order & refund buyer"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Buyer cancel dialog — no strike, full refund, listing relisted */}
      <Dialog open={buyerCancel.open} onOpenChange={(v) => setBuyerCancel((s) => ({ ...s, open: v }))}>
        <DialogContent className="bg-surface border-sniper text-foreground rounded-none" data-testid="buyer-cancel-dialog">
          <DialogHeader><DialogTitle className="font-heading uppercase flex items-center gap-2"><XCircle className="w-5 h-5 text-olive-bright" /> Cancel your order</DialogTitle></DialogHeader>
          <DialogDescription className="text-xs font-mono uppercase text-muted-foreground">
            You can cancel because the seller hasn't shipped yet. You'll be refunded in full — bank posting time varies.
          </DialogDescription>
          <textarea rows={2} data-testid="buyer-cancel-reason" value={buyerCancel.reason}
            onChange={(e) => setBuyerCancel((s) => ({ ...s, reason: e.target.value }))}
            placeholder="Reason (optional)…" className="w-full bg-input border border-sniper focus:border-olive outline-none px-3 py-2 text-foreground font-body text-sm transition-colors" />
          <DialogFooter>
            <button onClick={submitBuyerCancel} disabled={busy} data-testid="buyer-cancel-submit"
              className="bg-olive hover:bg-olive-bright disabled:opacity-50 text-white font-heading font-bold uppercase px-6 py-2.5 transition-colors">
              {busy ? "Cancelling…" : "Cancel & refund me"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
